import { useRef } from 'react';
import { AnimatePresence, useInView } from 'framer-motion';
import { StaticImage } from 'gatsby-plugin-image';
import { rgba } from 'polished';
import styled from 'styled-components';

import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import { INTERNAL_LINKS } from '@/utils';

import { backgroundImageHatched, ContentMarginLine } from '../v2/GridLines';
import { LayoutGridCondensed } from '../v2/Page/LayoutGrid';

import { config } from './config';
import { useIncrementSelection, Workflow } from './Workflow';

const STATIC_IMG_PATH = '../../../static/images/home-2025';

const OPTIONS = [
  {
    value: 0,
    title: 'From insight to interface',
    copy: (
      <Workflow.OptionContent>
        Build polished data apps with drag-and-drop components.
        <Workflow.OptionButton
          disableCornerLines
          link={INTERNAL_LINKS.AppBuilder}
          text="Learn more about data apps"
        />
      </Workflow.OptionContent>
    ),
    media: (
      <Workflow.IllustrationImg>
        <StaticImage
          alt="Drag and drop illustration"
          sizes="(min-width: 800px) 50vw, 100vw"
          src={`${STATIC_IMG_PATH}/data-apps/1.png`}
          transformOptions={{ fit: 'contain' }}
        />
      </Workflow.IllustrationImg>
    )
  },
  {
    value: 1,
    title: 'Data experiences that actually do something',
    copy: (
      <Workflow.OptionContent>
        Add inputs, write back to your database, and power apps with live models
        to turn analysis into action.
      </Workflow.OptionContent>
    ),
    media: (
      <Workflow.IllustrationImg>
        <StaticImage
          alt="Data experiences illustration"
          sizes="(min-width: 800px) 50vw, 100vw"
          src={`${STATIC_IMG_PATH}/data-apps/2.png`}
          transformOptions={{ fit: 'contain' }}
        />
      </Workflow.IllustrationImg>
    )
  },
  {
    value: 2,
    title: 'Shorten the feedback loop',
    copy: (
      <Workflow.OptionContent>
        Share apps for fast, real-time feedback, shortening the loop between
        insight and decision.
        <Workflow.OptionButton
          disableCornerLines
          link={INTERNAL_LINKS.ForTeams}
          text="Learn how Hex brings everyone together"
        />
      </Workflow.OptionContent>
    ),
    media: (
      <Workflow.IllustrationImg>
        <StaticImage
          alt="Shorten the feedback loop illustration"
          sizes="(min-width: 800px) 50vw, 100vw"
          src={`${STATIC_IMG_PATH}/data-apps/3.png`}
          transformOptions={{ fit: 'contain' }}
        />
      </Workflow.IllustrationImg>
    )
  },
  {
    value: 3,
    title: 'Turn one-offs into building blocks',
    copy: (
      <Workflow.OptionContent>
        Save and reuse components across apps to scale your work without
        starting from scratch.
      </Workflow.OptionContent>
    ),
    media: (
      <Workflow.IllustrationImg data-bottom>
        <StaticImage
          alt="reusable components illustration"
          sizes="(min-width: 800px) 50vw, 100vw"
          src={`${STATIC_IMG_PATH}/data-apps/4.png`}
          transformOptions={{ fit: 'contain' }}
        />
      </Workflow.IllustrationImg>
    )
  }
];

const StyledContentMarginLine = styled(ContentMarginLine)`
  --border-color: ${({ theme }) => theme.palette.fg600};

  &:not(&[data-right]) {
    background-image: ${({ theme }) => backgroundImageHatched(theme)};
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

export function DataApps() {
  const ref = useRef<HTMLFieldSetElement>(null);
  const isInView = useInView(ref, {
    margin: '-160px 0px 0px 0px',
    amount: 0.25
  });
  const { increment, option, value, set } = useIncrementSelection(OPTIONS);

  return (
    <Workflow.Section id={config.workflows.data_apps.anchor}>
      <LayoutGridCondensed>
        <StyledContentMarginLine />
        <Workflow.Content>
          <Workflow.TagLink
            className={textStyles.annotation}
            data-apps
            to={INTERNAL_LINKS.AppBuilder}>
            {config.workflows.data_apps.icon} Data Apps
          </Workflow.TagLink>
          <Workflow.Headline>Powerful, interactive data apps</Workflow.Headline>
          <Workflow.SubHeadline>
            From simple reports to complex workflows, easily build apps that
            allow business stakeholders to understand and take action.
          </Workflow.SubHeadline>

          <Workflow.Selector ref={ref} color={config.workflows.data_apps.color}>
            {OPTIONS.map(opt => (
              <Workflow.Option
                key={opt.value}
                checked={value === opt.value}
                group={config.workflows.data_apps.anchor}
                isInView={isInView}
                title={opt.title}
                value={opt.value}
                onAnimationComplete={increment}
                onChange={() => {
                  set(opt.value);
                }}>
                {opt.copy}
              </Workflow.Option>
            ))}
          </Workflow.Selector>
        </Workflow.Content>
        <Workflow.IllustrationArea
          style={{
            '--workflow-border-color': rgba(
              config.workflows.data_apps.color,
              0.2
            )
          }}>
          <Workflow.IllustrationContainer>
            <AnimatePresence mode="popLayout">
              <Workflow.IllustrationPresenceAnim key={option.value}>
                {option.media}
              </Workflow.IllustrationPresenceAnim>
            </AnimatePresence>
          </Workflow.IllustrationContainer>
        </Workflow.IllustrationArea>
        <StyledContentMarginLine data-right />
      </LayoutGridCondensed>
    </Workflow.Section>
  );
}
